import React from 'react';
import AdminTestPanel from '../../components/AdminTestPanel';
import AdminReadyButton from '../../components/AdminReadyButton';
import strings from '../../strings';
import {getSetningsMinne} from '../../constants/tests.js';
import {updateReady, updateStepData} from '../../api';

const SM_STAGES = {
	INTRO: 0,
	TRIAL: 1,
	WAITING_POST_TRIAL: 2,
	TEST: 3,
	WAITING: 4,
};

// ----- Scoring map for TEST -----
const pointsFromMistakes = (mistakes) => {
	// new scoring provided by Trude. This needs to change
	if (mistakes <= 0) return 4;
	if (mistakes === 1) return 3;
	if (mistakes === 2) return 2;
	if (mistakes === 3) return 1;
	return 0; // >=4
};
const isZeroPoint = (mistakes) => pointsFromMistakes(mistakes) === 0;

class SentenceMemoryAdmin extends AdminTestPanel {
	constructor(props) {
		super(props, {
			stopOnConsecutiveErrors: false, // Only applies to TEST (0 points)
			maxConsecutiveErrors: 13,
			enableTimers: false,
			enableHotkeys: true,
			limitHotkeysToTest: true,
		});
		this.rootEl = null;
		// state from AdminTestPanel + our additions
		this.state = {
			...this.state,
			currentStage: SM_STAGES.INTRO,
			currentStep: 0,
			mistakes: 0,
			results: {
				trial: {}, // per-step: { isCorrect, isAnswered }
				test: {}, // per-step: { mistakes, points, isAnswered }
			},
			consecutiveErrors: 0,
		};

		const sm = getSetningsMinne();
		this.trialSteps = sm.getTrialSteps(
			this.props.testKeyType,
			this.props.ageGroup
		);
		this.testSteps = sm.getSentenceSteps(
			this.props.testKeyType,
			this.props.ageGroup
		);
	}
	componentDidMount() {
		updateReady(false);
		if (this.rootEl) {
			this.rootEl.focus();
		}
	}

	componentDidUpdate(prevProps) {
		const parentProto = Object.getPrototypeOf(
			SentenceMemoryAdmin.prototype
		);
		if (
			parentProto &&
			typeof parentProto.componentDidUpdate === 'function'
		) {
			parentProto.componentDidUpdate.call(this, prevProps);
		}

		// handles step change
		if (prevProps.currentStep !== this.props.currentStep) {
			this.setState({currentStep: this.props.currentStep, mistakes: 0});
		}

		// handles stage change
		if (prevProps.currentStage !== this.props.currentStage) {
			this.setState({currentStage: this.props.currentStage, mistakes: 0});
		}

		// trial feedback handshake
		if (this.state.currentStage === SM_STAGES.TRIAL) {
			var prevSD = prevProps.stepData || {};
			var nextSD = this.props.stepData || {};
			if (
				nextSD.feedbackDone &&
				!prevSD.feedbackDone &&
				nextSD.answered
			) {
				if (nextSD.correct === true) {
					// PRAISE path: move to next trial item (no backend result updates)
					this.setState({mistakes: 0}, () => this.props.nextStep());
				} else {
					// TRY AGAIN path: stay on same item, allow admin to re-judge
					this.setState({mistakes: 0});
					// Clear stepData so a new submission retriggers feedback
					updateStepData({
						answered: false,
						correct: null,
						feedbackDone: false,
					});
				}
			}
		}
	}

	// ======== TRIAL actions ========
	answer = (numberOfCorrect) => {
		if (numberOfCorrect === 1) {
			updateStepData({
				answered: true,
				correct: true,
				feedbackDone: false,
			});
		} else {
			updateStepData({
				answered: true,
				correct: false,
				feedbackDone: false,
			});
		}
	};

	submitTrialMistakes = () => {
		const m = this.state.mistakes;
		const correctFlag = m <= 0 ? 1 : 0; // 1 = correct feedback, 0 = incorrect
		this.answer(correctFlag); // triggers feedback
		this.setState({mistakes: 0}); // reset counter
		this.props.nextStep();
	};

	commitTrialCorrect = () => {
		const {currentStep, results} = this.state;

		const updated = {
			...results,
			trial: {
				...results.trial,
				[currentStep]: {isCorrect: true, isAnswered: true},
			},
		};

		this.setState({results: updated}, () => this.props.nextStep(updated));
	};

	answerUnansweredTrial = () => {
		const {currentStep, results} = this.state;

		const updated = {
			...results,
			trial: {
				...results.trial,
				[currentStep]: {isCorrect: false, isAnswered: false},
			},
		};

		this.setState({results: updated}, () => this.props.nextStep(updated));
	};

	incrementMistakes = () =>
		this.setState(({mistakes}) => ({mistakes: Math.min(mistakes + 1, 10)}));

	decrementMistakes = () =>
		this.setState(({mistakes}) => ({mistakes: Math.max(mistakes - 1, 0)}));

	submitMistakes = () => {
		const {mistakes, currentStep, results, consecutiveErrors, options} =
			this.state;
		const points = pointsFromMistakes(mistakes);

		const updated = {
			...results,
			test: {
				...results.test,
				[currentStep]: {mistakes, points, isAnswered: true},
			},
		};

		const nextConsec = isZeroPoint(mistakes) ? consecutiveErrors + 1 : 0;

		this.setState(
			{results: updated, consecutiveErrors: nextConsec, mistakes: 0},
			() => {
				if (
					nextConsec >= options.maxConsecutiveErrors &&
					options.stopOnConsecutiveErrors
				) {
					this.props.submitResults(this.state.results);
					this.props.nextStage(); // to WAITING
				} else {
					this.props.nextStep(this.state.results);
				}
			}
		);
	};

	markUnanswered = () => {
		const {results, currentStep, consecutiveErrors, options} = this.state;

		const updated = {
			...results,
			test: {
				...results.test,
				[currentStep]: {mistakes: null, points: 0, isAnswered: false},
			},
		};

		const nextConsec = consecutiveErrors + 1;

		this.setState(
			{results: updated, consecutiveErrors: nextConsec, mistakes: 0},
			() => {
				if (
					nextConsec >= options.maxConsecutiveErrors &&
					options.stopOnConsecutiveErrors
				) {
					this.props.submitResults(this.state.results);
					this.props.nextStage();
				} else {
					this.props.nextStep(this.state.results);
				}
			}
		);
	};

	handleKeyDown = (e) => {
		const {enableHotkeys, limitHotkeysToTest} = this.state.options;
		if (!enableHotkeys) return;

		const {currentStage} = this.state;
		const inTrial = currentStage === SM_STAGES.TRIAL;
		const inTest = currentStage === SM_STAGES.TEST;

		if (inTrial) {
			if (e.key === '+') {
				this.incrementMistakes();
				return;
			}
			if (e.key === '-') {
				this.decrementMistakes();
				return;
			}
			if (e.key === 'Enter') {
				this.submitTrialMistakes();
				return;
			}
			if (
				e.key &&
				typeof e.key === 'string' &&
				e.key.toLowerCase() === 'u'
			) {
				this.answerUnansweredTrial();
				return;
			}
			if (/^[0-9]$/.test(e.key)) {
				var mSet = Math.min(Number(e.key), 9);
				this.setState({mistakes: mSet});
				return;
			}

			if (e.key === 'a') {
				this.setState({mistakes: 0}, this.submitTrialMistakes);
				return;
			}
			if (e.key === 'l') {
				this.setState({mistakes: 1}, this.submitTrialMistakes);
				return;
			}
			if (e.key === 'k') {
				this.answerUnansweredTrial();
				return;
			}
			return;
		}

		if (limitHotkeysToTest && !inTest) return;

		if (e.key === '+') this.incrementMistakes();
		else if (e.key === '-') this.decrementMistakes();
		else if (e.key === 'Enter') this.submitMistakes();
		else if (
			e.key &&
			typeof e.key === 'string' &&
			e.key.toLowerCase() === 'u'
		) {
			this.markUnanswered();
		} else if (/^[0-9]$/.test(e.key)) {
			const m = Math.min(Number(e.key), 9);
			this.setState({mistakes: m});
		}
	};

	// ======== Render ========
	renderIntro = () => {
		const {testKeyType = null} = this.props;

		return (
			<>
				<p>{strings.sm_admin1}</p>
				<p>{strings.sm_admin2}</p>

				<AdminReadyButton
					isPractiseTasks
					remoteIsReady={this.props.candidateReady}
					onClick={this.props.nextStep}
					testKeyType={testKeyType}
				/>
			</>
		);
	};

	renderTrial = function () {
		const currentStep = this.state.currentStep;
		const mistakes = this.state.mistakes;
		const dataset = this.trialSteps || [];

		const total = dataset.length;
		const expected =
			dataset && dataset[currentStep] ? String(dataset[currentStep]) : '';

		const points = pointsFromMistakes(mistakes);

		const previewButton =
			mistakes <= 0 ? strings.correctLabel : strings.incorrectLabel;
		return (
			<div>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_1}
				</p>

				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_title}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_a}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_b}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_c}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_d}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_e}
				</p>

				<p className="sm-step-progress">
					{strings.formatString(
						strings.trialTaskNumOf,
						currentStep + 1,
						total
					)}
				</p>

				<div className="sm-expected">
					<strong>{strings.nonword_admin_expectedAnswer}</strong>
					<span className="sm-expected__text">{expected}</span>
				</div>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_2}
				</p>
				<p className="step-instructions">
					{strings.formatString(
						strings.sm_trial_admin_info_4,
						expected
					)}
				</p>
				<div className="sm-admin-controls">
					<div className="sm-mistake-counter">
						{strings.sm_mistakes_label}:{' '}
						<span className="sm-mistake-counter__value">
							{mistakes}
						</span>
					</div>
					<button
						onClick={this.decrementMistakes}
						disabled={mistakes <= 0}
					>
						−
					</button>
					<button onClick={this.incrementMistakes}>+</button>

					<span className="sm-points-badge">{points}p</span>
					<button
						className="sm-btn sm-btn--primary"
						onClick={this.submitTrialMistakes}
					>
						{previewButton}
					</button>

					<button
						className="sm-btn sm-btn--ghost"
						onClick={() => this.setState({mistakes: 0})}
					>
						{strings.reset}
					</button>

					<button
						className="sm-btn sm-btn--ghost"
						onClick={this.answerUnansweredTrial}
					>
						{strings.unAnswered}
					</button>
				</div>
			</div>
		);
	};

	renderWaitingPostTrial = () => (
		<div>
			<div className="lit-all-buttons-div">
				<button
					onClick={this.props.nextStage}
					style={{width: '100px', marginTop: '10px'}}
				>
					{strings.startAdult}
				</button>
			</div>
		</div>
	);

	renderTest = () => {
		const {currentStep, mistakes} = this.state;
		const dataset = this.testSteps || [];
		const total = (this.testSteps || []).length;
		const expected =
			dataset && dataset[currentStep] ? String(dataset[currentStep]) : '';

		const points = pointsFromMistakes(mistakes);
		const previewButton =
			mistakes <= 0 ? strings.correctLabel : strings.incorrectLabel;

		return (
			<div>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_1}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_title}
				</p>

				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_a}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_b}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_c}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_d}
				</p>
				<p className="step-instructions">
					{strings.sm_trial_admin_info_3_e}
				</p>

				<p className="sm-step-progress">
					{strings.formatString(
						strings.taskNumOf,
						currentStep + 1,
						total
					)}
				</p>

				<div className="sm-expected">
					<strong>{strings.nonword_admin_expectedAnswer}</strong>
					<span className="sm-expected__text">{expected}</span>
				</div>

				<div className="sm-admin-controls">
					<div className="sm-mistake-counter">
						{strings.sm_mistakes_label}:{' '}
						<span className="sm-mistake-counter__value">
							{mistakes}
						</span>
					</div>

					{/* live points preview */}
					<button
						onClick={this.decrementMistakes}
						disabled={mistakes <= 0}
					>
						−
					</button>
					<button onClick={this.incrementMistakes}>+</button>
					<span className="sm-points-badge">{points}p</span>

					{/* confirm & next uses current mistakes */}
					<button
						className="sm-btn sm-btn--primary"
						onClick={this.submitMistakes}
					>
						{previewButton}
					</button>

					{/* optional reset to zero mistakes before confirming */}
					<button
						className="sm-btn sm-btn--ghost"
						onClick={() => this.setState({mistakes: 0})}
					>
						{strings.reset}
					</button>

					{/* unanswered & next */}
					<button
						className="sm-btn sm-btn--ghost"
						onClick={this.markUnanswered}
					>
						{strings.unAnswered}
					</button>
				</div>
			</div>
		);
	};

	renderWaiting = () => (
		<div>
			<p>
				{strings.formatString(
					strings.testDoneAdult4,
					strings.sm_testname
				)}
			</p>
			<button onClick={this.props.nextStage}>{strings.nextTest}</button>
		</div>
	);

	render() {
		const {currentStage} = this.state;
		return (
			<div
				className="lit-intro SetningsMinneAdmin"
				// onKeyDown={this.handleKeyDown}
				tabIndex={0}
				ref={(el) => (this.rootEl = el)}
			>
				<h2 className="lit-page-title">{strings.sm_testname}</h2>

				{currentStage === SM_STAGES.INTRO && this.renderIntro()}
				{currentStage === SM_STAGES.TRIAL && this.renderTrial()}
				{currentStage === SM_STAGES.WAITING_POST_TRIAL &&
					this.renderWaitingPostTrial()}
				{currentStage === SM_STAGES.TEST && this.renderTest()}
				{currentStage === SM_STAGES.WAITING && this.renderWaiting()}
			</div>
		);
	}
}

export default SentenceMemoryAdmin;
